/**
 * Editor Re-check (verapdf-in-editor-260509 §14 Path A).
 *
 * POSTs the in-memory edited PDF (via state.interop.serializeToBytes())
 * to /api/editor/recheck and renders the returned veraPDF report in
 * the inspector sidebar.
 *
 * Soft-lag mechanism (P-3): the SOLE rate limit is a client-side
 * `await sleep(20000 - elapsed)` when a second click arrives within
 * 20s of the previous one. NO 429, NO toast, NO modal — just feels
 * less snappy. Per design §14.3.
 *
 * Public surface:
 *   createRecheckController({ getPdfBytes, els, fetchImpl, sleepImpl,
 *                              nowImpl, docImpl, FormDataCtor, BlobCtor }) → controller
 *
 * Where:
 *   getPdfBytes()  → Promise<Uint8Array>     // serializes current edit
 *   els.btn        → HTMLButtonElement       // the Re-check button
 *   els.status     → HTMLElement             // status text container
 *   els.issues     → HTMLUListElement        // issue list container
 *   els.section    → HTMLElement             // toggleable container
 *   fetchImpl, sleepImpl, nowImpl → injectables for tests
 *   docImpl        → Document-like: { createElement } — injected for tests
 *   FormDataCtor   → FormData constructor — injected for tests
 *   BlobCtor       → Blob constructor — injected for tests
 */
const MIN_INTERVAL_MS = 20_000;
// ─── Implementation ───────────────────────────────────────────────────────────
export function createRecheckController({ getPdfBytes, els, fetchImpl = (url, init) => fetch(url, init), sleepImpl = (ms) => new Promise((r) => setTimeout(r, ms)), nowImpl = () => Date.now(), 
// WHY: defaults resolved lazily inside the function so production callers
// that omit these get the real browser globals while Node tests can inject
// stubs without triggering a ReferenceError (document/FormData/Blob are
// undefined in Node's test environment).
docImpl = undefined, FormDataCtor = undefined, BlobCtor = undefined, }) {
    /* eslint-disable no-undef */
    // Resolve globals at call time, not at module-load time, so they work in
    // browsers and are suppressible in Node tests via the injection params.
    const _doc = docImpl || (typeof document !== "undefined" ? document : null);
    const _FormData = FormDataCtor || (typeof FormData !== "undefined" ? FormData : null);
    const _Blob = BlobCtor || (typeof Blob !== "undefined" ? Blob : null);
    /* eslint-enable no-undef */
    if (!els || !els.btn) {
        throw new Error("createRecheckController requires els.btn");
    }
    let lastRecheckMs = 0;
    let inFlight = false;
    function setStatus(text, isError = false) {
        if (!els.status)
            return;
        els.status.textContent = text;
        els.status.classList.toggle("is-error", !!isError);
    }
    function renderIssues(report) {
        if (!els.issues || !_doc)
            return;
        els.issues.innerHTML = "";
        const issues = (report && Array.isArray(report.issues)) ? report.issues : [];
        if (issues.length === 0) {
            const li = _doc.createElement("li");
            li.className = "editor-recheck-empty";
            const total = report && report.total_failed_checks;
            if (typeof total === "number" && total > 0) {
                // veraPDF capped sample but reported real failures.
                li.textContent = `${total} issue(s) reported (samples capped).`;
            }
            else {
                li.textContent = "No issues found.";
            }
            els.issues.appendChild(li);
            return;
        }
        for (const issue of issues) {
            const li = _doc.createElement("li");
            const ruleSpan = _doc.createElement("span");
            ruleSpan.className = "editor-recheck-issue-rule";
            ruleSpan.textContent = String(issue.rule || issue.specification || "?");
            li.appendChild(ruleSpan);
            if (issue.page != null) {
                const pageSpan = _doc.createElement("span");
                pageSpan.className = "editor-recheck-issue-page";
                pageSpan.textContent = `p.${issue.page}`;
                li.appendChild(pageSpan);
            }
            const msgSpan = _doc.createElement("span");
            msgSpan.className = "editor-recheck-issue-msg";
            msgSpan.textContent = String(issue.message || issue.description || "");
            li.appendChild(msgSpan);
            els.issues.appendChild(li);
        }
    }
    async function trigger() {
        if (inFlight)
            return undefined; // guard duplicate clicks while a fetch is open
        // Soft-lag: invisible delay that throttles to one fetch per 20s.
        const elapsed = nowImpl() - lastRecheckMs;
        if (elapsed < MIN_INTERVAL_MS) {
            await sleepImpl(MIN_INTERVAL_MS - elapsed);
        }
        inFlight = true;
        if (els.btn) {
            els.btn.disabled = true;
            els.btn.setAttribute("aria-busy", "true");
        }
        setStatus("Checking…", false);
        try {
            const bytes = await getPdfBytes();
            if (!(bytes instanceof Uint8Array) || bytes.length === 0) {
                throw new Error("No PDF bytes available");
            }
            if (!_FormData || !_Blob) {
                throw new Error("FormData/Blob unavailable in this environment");
            }
            const form = new _FormData();
            form.append("file", new _Blob([bytes.buffer], { type: "application/pdf" }), "edited.pdf");
            const resp = await fetchImpl("/api/editor/recheck", {
                method: "POST",
                body: form,
                credentials: "same-origin",
            });
            // Update the timestamp BASED ON COMPLETION (so the lag tracks
            // the end of the previous request, not the start of this one).
            lastRecheckMs = nowImpl();
            const body = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const err = (body && body["error"])
                    || (body && body["detail"])
                    || `HTTP ${resp.status}`;
                setStatus(`Re-check failed: ${err}`, true);
                renderIssues(null);
                return { ok: false, status: resp.status, error: String(err) };
            }
            const report = body && body["report"];
            renderIssues(report ?? null);
            const count = (report && Array.isArray(report.issues))
                ? report.issues.length : 0;
            const total = (report && typeof report.total_failed_checks === "number")
                ? report.total_failed_checks : count;
            setStatus(`Re-check complete: ${total} issue(s).`, false);
            return { ok: true, status: resp.status, report };
        }
        catch (err) {
            const msg = (err instanceof Error) ? err.message : String(err);
            setStatus(`Re-check failed: ${msg}`, true);
            renderIssues(null);
            return { ok: false, status: 0, error: msg };
        }
        finally {
            inFlight = false;
            if (els.btn) {
                els.btn.disabled = false;
                els.btn.removeAttribute("aria-busy");
            }
        }
    }
    if (els.btn) {
        els.btn.addEventListener("click", () => { void trigger(); });
    }
    return {
        trigger,
        get lastRecheckMs() { return lastRecheckMs; },
        get inFlight() { return inFlight; },
        // Test-only: reset internal state.
        _reset() { lastRecheckMs = 0; inFlight = false; },
    };
}
// ─── Boot wiring ─────────────────────────────────────────────────────────────
/**
 * Wire the Re-check section into the editor at boot.
 */
// eslint-disable-next-line @typescript-eslint/require-await -- async signature required for callers that await initRecheck; body is sync
export async function initRecheck({ getPdfBytes, doc = typeof document !== "undefined" ? document : undefined, } = {}) {
    if (!doc)
        return null;
    const section = doc.getElementById("editor-recheck-section");
    const btn = doc.getElementById("editor-recheck-btn");
    const status = doc.getElementById("editor-recheck-status");
    const issues = doc.getElementById("editor-recheck-issues");
    if (!section || !btn)
        return null;
    section.classList.remove("hidden");
    return createRecheckController({
        getPdfBytes,
        els: { btn: btn, status, issues, section },
        docImpl: doc,
    });
}
export const RECHECK_MIN_INTERVAL_MS = MIN_INTERVAL_MS;
